/**
 * Field map for the official 2024 D&D character sheet PDF.
 *
 * The form's fields are auto-named (Text1, Check Box5…), so this map was built
 * by stamping every widget with its own name, rendering the sheet, and reading
 * off the labels. Do not reorder — the names are the only thing tying a value
 * to a box on the page.
 */
import type { Ability, SkillKey } from '@shared/rules/constants'

export const PAGE1 = {
  name: 'Text1',
  background: 'Text6',
  className: 'Text7',
  species: 'Text8',
  subclass: 'Text9',
  level: 'Text11',
  xp: 'Text12',

  armorClass: 'Text13',
  shield: 'Check Box3',

  hpCurrent: 'Text14',
  hpTemp: 'Text15',
  hpMax: 'Text16',
  hitDiceMax: 'Text17',
  hitDiceSpent: 'Text18',
  deathSaveSuccesses: ['Check Box5', 'Check Box6', 'Check Box7'],
  deathSaveFailures: ['Check Box8', 'Check Box9', 'Check Box10'],

  proficiencyBonus: 'Text19',
  initiative: 'Text26',
  speed: 'Text27',
  size: 'Text28',
  passivePerception: 'Text29',
  heroicInspiration: 'Check Box11',

  classFeaturesLeft: 'Text54',
  classFeaturesRight: 'Text55',
  speciesTraits: 'Text57',
  feats: 'Text58',
  weaponProficiencies: 'Text59',
  tools: 'Text60',

  armorTraining: {
    light: 'Check Box13',
    medium: 'Check Box14',
    heavy: 'Check Box15',
    shields: 'Check Box12',
  },
} as const

/** [modifier field, score field] per ability. */
export const ABILITY_FIELDS: Record<Ability, [string, string]> = {
  str: ['Text21', 'Text64'],
  dex: ['Text22', 'Text66'],
  con: ['Text24', 'Text67'],
  int: ['Text20', 'Text63'],
  wis: ['Text23', 'Text65'],
  cha: ['Text25', 'Text68'],
}

/** [proficiency checkbox, bonus field] per saving throw. */
export const SAVE_FIELDS: Record<Ability, [string, string]> = {
  str: ['Check Box37', 'Text91'],
  dex: ['Check Box33', 'Text87'],
  con: ['Check Box32', 'Text86'],
  int: ['Check Box4', 'Text69'],
  wis: ['Check Box21', 'Text75'],
  cha: ['Check Box26', 'Text81'],
}

/** [proficiency checkbox, bonus field] per skill. */
export const SKILL_FIELDS: Record<SkillKey, [string, string]> = {
  athletics: ['Check Box38', 'Text92'],
  acrobatics: ['Check Box34', 'Text88'],
  sleight_of_hand: ['Check Box35', 'Text89'],
  stealth: ['Check Box36', 'Text90'],
  arcana: ['Check Box16', 'Text70'],
  history: ['Check Box17', 'Text71'],
  investigation: ['Check Box19', 'Text72'],
  nature: ['Check Box20', 'Text73'],
  religion: ['Check Box18', 'Text74'],
  animal_handling: ['Check Box22', 'Text76'],
  insight: ['Check Box23', 'Text77'],
  medicine: ['Check Box25', 'Text78'],
  perception: ['Check Box31', 'Text79'],
  survival: ['Check Box24', 'Text80'],
  deception: ['Check Box27', 'Text82'],
  intimidation: ['Check Box28', 'Text83'],
  performance: ['Check Box30', 'Text84'],
  persuasion: ['Check Box29', 'Text85'],
}

/** Six rows of [name, attack bonus, damage, notes]. */
export const WEAPON_ROWS: [string, string, string, string][] = [
  ['Text30', 'Text31', 'Text32', 'Text33'],
  ['Text34', 'Text35', 'Text36', 'Text37'],
  ['Text38', 'Text39', 'Text40', 'Text41'],
  ['Text42', 'Text43', 'Text44', 'Text45'],
  ['Text46', 'Text47', 'Text48', 'Text49'],
  ['Text50', 'Text51', 'Text52', 'Text53'],
]

export const PAGE2 = {
  spellcastingAbility: 'Text111',
  spellcastingModifier: 'Text93',
  spellSaveDC: 'Text94',
  spellAttackBonus: 'Text95',

  appearance: 'Text96',
  backstory: 'Text97',
  alignment: 'Text100',
  languages: 'Text98',
  equipment: 'Text99',

  attunement: ['Text101', 'Text102', 'Text103'],
  coins: { cp: 'Text226', sp: 'Text267', ep: 'Text268', gp: 'Text269', pp: 'Text270' },
} as const

/** Spell slot totals, indexed 1-9. The field numbers are not in level order. */
export const SPELL_SLOT_FIELDS: Record<number, string> = {
  1: 'Text112', 2: 'Text113', 3: 'Text114',
  4: 'Text117', 5: 'Text116', 6: 'Text115',
  7: 'Text118', 8: 'Text119', 9: 'Text120',
}

/** The Notes column runs out of order partway down the table. */
const SPELL_NOTE_FIELDS = [
  'Text108', 'Text208', 'Text209', 'Text210', 'Text211', 'Text212', 'Text213',
  'Text214', 'Text215', 'Text216', 'Text217', 'Text218', 'Text219', 'Text220',
  'Text221', 'Text222', 'Text223', 'Text224', 'Text225', 'Text227', 'Text228',
  'Text229', 'Text230', 'Text244', 'Text231', 'Text232', 'Text233', 'Text234',
  'Text235', 'Text236',
]

export interface SpellRowFields {
  level: string
  name: string
  castingTime: string
  range: string
  concentration: string
  ritual: string
  material: string
  notes: string
}

/**
 * The 30 spell rows. Checkbox names restart at three points down the table
 * (rows 0-6, 7-19, 20-29), which is why this is generated rather than listed.
 */
export const SPELL_ROWS: SpellRowFields[] = Array.from({ length: 30 }, (_, row) => {
  let concentration: string
  let ritual: string
  let material: string

  if (row <= 6) {
    concentration = `Check Box252.${row}`
    ritual = `Check Box253.${row}`
    material = `Check Box254.0.${row}`
  } else if (row <= 19) {
    concentration = `Check Box255.${row - 7}`
    ritual = `Check Box256.${row - 7}`
    material = `Check Box257.${row - 7}`
  } else {
    concentration = `Check Box258.${row - 20}`
    ritual = `Check Box259.${row - 20}`
    material = `Check Box260.${row - 20}`
  }

  return {
    level: `Text105.${row}`,
    name: `Text106.${row}`,
    castingTime: `Text107.${row}`,
    range: `Text109.${row}`,
    concentration,
    ritual,
    material,
    notes: SPELL_NOTE_FIELDS[row]!,
  }
})

export const SHEET_PDF_URL = '/assets/character-sheet.pdf'
